import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/ui-kit/atoms/button';
import { Modal } from '@/ui-kit/molecules/modal';
import {
  captureDomSnapshot,
  sendOneShot,
} from './console-capture';
import { onBugReportRequested } from './bug-report-bus';

type Snapshot = ReturnType<typeof captureDomSnapshot>;

/**
 * Header bug-report button. Visible only to staff (caller gates by
 * privilege). On click: snapshots the current main element's DOM +
 * recent console history BEFORE opening the modal, so the snapshot
 * reflects the page the user was on, not the modal we're opening.
 *
 * One of three trigger paths for a bug report:
 *   1. This button (manual)
 *   2. Auto-capture on uncaught browser errors / api errors (see
 *      console-capture.ts + the QueryClient onError hook in main.tsx)
 *   3. Server-side errors (Fastify onError hook in api/server.ts)
 * All three flow into the same _client_events table.
 */
export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState('');
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  const onClick = useCallback(() => {
    // Capture BEFORE opening the modal — modal mount adds DOM that we
    // don't want in the snapshot. Synchronous: same tick as the click.
    setSnapshot(captureDomSnapshot(document.querySelector('main')));
    setDescription('');
    setOpen(true);
  }, []);

  // Any open page modal can ask for this form, because a modal makes the
  // header button INERT (see ui-kit/molecules/modal.tsx). One owner of the
  // snapshot + description state, many triggers.
  useEffect(() => onBugReportRequested(onClick), [onClick]);

  const onSubmit = async () => {
    if (!snapshot) return;
    setSubmitting(true);
    try {
      // Two-call submit: details first (small, must land), snapshot second
      // (large, best-effort). Linked by a bugId so the api can join them in
      // the archive view. The first call also carries the user-visible
      // message — that's what shows up in the api stdout / Monitor stream.
      const bugId = crypto.randomUUID();
      const ts = Date.now();
      await sendOneShot({
        type: 'bug_report',
        level: 'warn',
        ts,
        message: description || '(no description)',
        payload: {
          bugId,
          description,
          source:              'manual',
          snapshotPending:     true,
          snapshotRoute:       snapshot.route,
          snapshotDomBytes:    snapshot.domBytes,
          snapshotOpenDialogs: snapshot.openDialogs.map((d) => d.title),
        },
      });
      setOpen(false);
      // Fire-and-forget — UI doesn't block on the snapshot upload.
      sendOneShot({
        type: 'bug_report_snapshot',
        level: 'info',
        ts,
        message: `snapshot for ${bugId}`,
        payload: { bugId, snapshot },
      }).catch((err) => {
        console.warn('[bug-report] snapshot upload failed', err);
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={onClick} title="Report a bug — captures current page state">
        🐞 Report bug
      </Button>
      <Modal title="Report a bug" size="max-w-2xl" open={open} onClose={() => setOpen(false)} hideBugReport
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button size="sm" onClick={onSubmit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit'}</Button>
          </>
        }
      >
        {snapshot && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              What happened?
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                autoFocus
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Describe what you were doing and what went wrong"
              />
            </label>
            <p className="text-xs text-gray-500">
              Captured snapshot of <code className="font-mono">{snapshot.route}</code> —{' '}
              {(snapshot.domBytes / 1024).toFixed(0)} KB DOM, {snapshot.recentLogs.length} recent console entries.
              {snapshot.openDialogs.length > 0 && (
                <>
                  {' '}Includes {snapshot.openDialogs.length} open dialog
                  {snapshot.openDialogs.length === 1 ? '' : 's'} at click time:{' '}
                  {snapshot.openDialogs.map((d, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      <span className="font-mono">&ldquo;{d.title}&rdquo;</span>
                    </span>
                  ))}.
                </>
              )}
              {' '}Staff-eyes-only.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
